import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
    Calendar,
    MapPin,
    Clock,
    Edit2,
    Trash2,
    User,
    Search,
    X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberEventsAdminPage = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, eventId: null, eventTitle: '' });
    const [editingEvent, setEditingEvent] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        date: '',
        time: '',
        location: '',
        event_type: 'hosting'
    });

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            const response = await axios.get(`${API}/admin/member-events`);
            setEvents(response.data);
        } catch (error) {
            toast.error('Failed to load member events');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (event) => {
        setEditingEvent(event);
        setFormData({
            title: event.title,
            description: event.description || '',
            date: event.date,
            time: event.time || '',
            location: event.location || '',
            event_type: event.event_type || 'hosting'
        });
        setEditModalOpen(true);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            await axios.put(`${API}/admin/member-events/${editingEvent.id}`, formData);
            toast.success('Event updated successfully');
            setEditModalOpen(false);
            setEditingEvent(null);
            fetchEvents();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update event');
        }
    };

    const handleDelete = async (eventId) => {
        try {
            await axios.delete(`${API}/admin/member-events/${eventId}`);
            toast.success('Event deleted successfully');
            setDeleteConfirm({ open: false, eventId: null, eventTitle: '' });
            fetchEvents();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete event');
        }
    };

    const formatDate = (dateStr) => {
        // Parse date string to avoid timezone issues
        // Expected format: YYYY-MM-DD
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return date.toLocaleDateString('en-US', { 
            weekday: 'long',
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    // Helper to parse date parts without timezone issues
    const getDateParts = (dateStr) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return {
            month: date.toLocaleDateString('en-US', { month: 'short' }),
            day: day
        };
    };

    const isPastEvent = (dateStr) => {
        // Parse date string to avoid timezone issues
        const [year, month, day] = dateStr.split('-').map(Number);
        const eventDate = new Date(year, month - 1, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day
        return eventDate < today;
    };

    const filteredEvents = events.filter(event =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.member?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-[#A0A0A0] animate-pulse">Loading member events...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white font-['Playfair_Display']">
                        Member Events
                    </h1>
                    <p className="text-[#A0A0A0] mt-1">Manage events shared by community members</p>
                </div>
                <div className="flex items-center gap-2">
                    <Badge className="bg-[#4A6B53] text-white">
                        {events.length} Events
                    </Badge>
                </div>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" />
                <Input
                    placeholder="Search events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-[#4A5240] border-[#5A6350] text-white placeholder:text-[#666]"
                />
            </div>

            {/* Events List */}
            {filteredEvents.length > 0 ? (
                <div className="space-y-4">
                    {filteredEvents.map((event) => (
                        <Card 
                            key={event.id}
                            className={`border-[#5A6350] ${isPastEvent(event.date) ? 'opacity-60' : ''}`}
                        >
                            <CardContent className="p-6">
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                    {/* Event Info */}
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className="text-lg font-semibold text-[#3D4435]">
                                                {event.title}
                                            </h3>
                                            {isPastEvent(event.date) ? (
                                                <Badge className="bg-gray-500 text-white">Past</Badge>
                                            ) : (
                                                <Badge className="bg-[#C9A227] text-[#3D4435]">Upcoming</Badge>
                                            )}
                                        </div>
                                        
                                        {event.description && (
                                            <p className="text-[#666] mb-3 line-clamp-2">{event.description}</p>
                                        )}

                                        <div className="flex flex-wrap gap-4 text-sm text-[#A0A0A0]">
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                <span>{formatDate(event.date)}</span>
                                            </div>
                                            {event.time && (
                                                <div className="flex items-center gap-1">
                                                    <Clock className="w-4 h-4" />
                                                    <span>{event.time}</span>
                                                </div>
                                            )}
                                            {event.location && (
                                                <div className="flex items-center gap-1">
                                                    <MapPin className="w-4 h-4" />
                                                    <span>{event.location}</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Posted By */}
                                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#E0E0E0]">
                                            <User className="w-4 h-4 text-[#A0A0A0]" />
                                            <span className="text-sm text-[#A0A0A0]">
                                                Posted by: <strong className="text-[#3D4435]">{event.member?.name || 'Unknown'}</strong>
                                                {event.member?.email && (
                                                    <span className="text-[#666]"> ({event.member.email})</span>
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleEdit(event)}
                                            className="border-[#5A6350]"
                                        >
                                            <Edit2 className="w-4 h-4 mr-1" />
                                            Edit
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setDeleteConfirm({ open: true, eventId: event.id, eventTitle: event.title })}
                                            className="border-[#A64444] text-[#A64444] hover:bg-[#A64444] hover:text-white"
                                        >
                                            <Trash2 className="w-4 h-4 mr-1" />
                                            Delete
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <Card className="border-[#5A6350]">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <Calendar className="w-16 h-16 text-[#5A6350] mb-4" />
                        <h3 className="text-xl font-semibold text-[#3D4435] mb-2">No Member Events</h3>
                        <p className="text-[#A0A0A0]">
                            {searchTerm ? 'No events match your search' : 'Members have not shared any events yet'}
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Edit Modal */}
            <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
                <DialogContent className="bg-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-[#3D4435] font-['Playfair_Display']">
                            Edit Member Event
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleUpdate} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Event Title *</Label>
                            <Input
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                                placeholder="Event title"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Event description"
                                rows={3}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Date *</Label>
                                <Input
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Time</Label>
                                <Input
                                    type="time"
                                    value={formData.time}
                                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Location</Label>
                            <Input
                                value={formData.location}
                                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                placeholder="Event location"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Event Type</Label>
                            <div className="flex flex-wrap gap-2">
                                {['hosting', 'attending', 'promoting'].map((type) => (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, event_type: type })}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                            formData.event_type === type
                                                ? 'bg-[#C9A227] text-[#3D4435]'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setEditModalOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                            >
                                Update Event
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, eventId: null, eventTitle: '' })}>
                <DialogContent className="bg-[#4A5240] border-[#5A6350] text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white font-['Playfair_Display']">
                            Delete Member Event
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-[#A0A0A0]">
                            Are you sure you want to delete <span className="text-white font-semibold">"{deleteConfirm.eventTitle}"</span>? 
                            This action cannot be undone.
                        </p>
                    </div>
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteConfirm({ open: false, eventId: null, eventTitle: '' })}
                            className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => handleDelete(deleteConfirm.eventId)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Delete Event
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MemberEventsAdminPage;
